import { observable, configure, action, computed, autorun, toJS } from 'mobx';
import util from 'preact-util';
import { route } from 'preact-router';

import localUtil from '../lib/util';
import { syncNativeStatusBar } from '../lib/nativeStatusBar';
import { isDarkmodeForced } from '../lib/darkmode';
import { appAlert } from '../lib/dialogs';

configure({ enforceActions: 'always' });

function merge(a, b, prop){
    const reduced =  a.filter(aitem => !b.find(bitem => aitem[prop] === bitem[prop]));
    return reduced.concat(b);
}

function scrollTo(element, top = 0, left = 0) {
    // element.scrollTop = to;
    element.scrollTo({
        top,
        left,
        behavior: 'smooth'
    });
}

function addMetaTag(tag = 'og:title', content = 'title') {
    var newTag = document.createElement('meta');
    newTag.setAttribute('property', tag);
    newTag.content = content
    document.getElementsByTagName('head')[0].appendChild(newTag);
}

function updateOrCreateMetaTag(propertyName, content, propertyType = 'name') {
    let selector = `${propertyType}="${propertyName}"`;
    let metaTag = document.querySelector(`meta[${selector}]`);

    if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(propertyType, propertyName);
        document.getElementsByTagName('head')[0].appendChild(metaTag);
    }

    metaTag.setAttribute('content', content);
}

function updateMetaTags(article) {
    if (!article) return;
    document.title = article.title; // Update the document title

    // Update or create each meta tag
    updateOrCreateMetaTag('description', article.description || article.ingress || '');
    updateOrCreateMetaTag('keywords', article.tags ? article.tags.map(tag => tag.name).join(', ') : '');

    updateOrCreateMetaTag('og:url', article.uuidv4 ? `/article/${encodeURIComponent(article.title).toLocaleLowerCase()}/${article.uuidv4}` : '', 'property');
    updateOrCreateMetaTag('og:type', article.type || 'article', 'property');
    updateOrCreateMetaTag('og:locale', 'no_NO', 'property');
    updateOrCreateMetaTag('og:title', article.title || '', 'property');
    updateOrCreateMetaTag('og:description', article.ingress || '', 'property');
    if (article.images && article.images[0]) {
        updateOrCreateMetaTag('thumbnail', article.images[0].s3LargeLink);
        updateOrCreateMetaTag('og:image', article.images[0].s3LargeLink, 'property');
        updateOrCreateMetaTag('og:image:width', '800', 'property');
        updateOrCreateMetaTag('og:image:type', 'image/jpeg', 'property');
    }
}

function getBrowserInfo() {
    const userAgent = navigator.userAgent;
    const language = navigator.language;

    // Basic parsing of userAgent to identify browser and OS - this can be expanded
    let browserName = userAgent.match(/firefox|fxios/i) ? 'Firefox' :
                      userAgent.match(/chrome|chromium|crios/i) ? 'Chrome' :
                      userAgent.match(/safari/i) ? 'Safari' :
                      userAgent.match(/opr\//i) ? 'Opera' :
                      userAgent.match(/edg/i) ? 'Edge' :
                      'Other';

    let osName = userAgent.match(/android/i) ? 'Android' :
                 userAgent.match(/iphone|ipad|ipod/i) ? 'iOS' :
                 userAgent.match(/win/i) ? 'Windows' :
                 userAgent.match(/mac/i) ? 'MacOS' :
                 userAgent.match(/linux/i) ? 'Linux' :
                 'Other';

    return {
        userAgent: userAgent,
        browser: browserName,
        os: osName,
        language: language
    };
}

class AppState {
    constructor() {
        this.setView(util.getObject('view'));
    }

    @observable opts = {
        skipRedirect: true,

        showCustomerLogo: false,
        showArticle: true,
        showMedia: true,
        showNews: true,
        showInstallation: true,
        showRace: true,
        showPart: true,

        showInspections: false,
        showAddInstallations: true,
        showSortParts: true,
        showAddParts: true,
        showTimeTracker: true,
        showDyrejournal: false,
        showFrontpageShop: false,

        swipeLeft: true,
        swipeRight: true,
        showLoan: true,
        showAddUsage: true,
        showMove: true,
        showDelete: true,
        showEdit: true,
        showRefresh: true,

        showAddToInstallation: true,
        showAddLog: true,
        showDeleteFromInstallation: true,
        allowEditQty: true,

        loginCountries: [
            'NO', // Norge
            'AR', // Argentina
            'AT', // Østerrike
            'BE', // Belgia
            'CH', // Sveits
            'CZ', // Tsjekkia
            'DE', // Tyskland
            'DK', // Danmark
            'EE', // Estland
            'ES', // Spania
            'FR', // Frankrike
            'GB', // Storbritannia
            'IE', // Irland
            'IT', // Italia
            'NL', // Nederland
            'PL', // Polen
            'SE', // Sverige
            'FI', // Finland
            'CA', // Canada
            'LT', // Litauen
            'SK', // Slovakia
            'US'  // USA
        ]
    };

    @observable isDevelopment = process.env.NODE_ENV === 'development';

    @observable state = 'active';

    @observable isVisibleNavbar = true;

    @observable hideGlobalFooter = false;

    @observable isMobile = true;

    @observable currentEmail = null;

    @observable isAdmin = false;

    @observable isExpert = false;

    @observable mousePos = [];

    @observable view = {};

    @observable inputProps = {};


    @observable newEmail = {};

    @observable newSms = {};

    @observable saved = {};

    @observable latlng = null;

    @observable mapColorMode = 'incline';

    @observable mapRange = [];

    @observable mapRangeMin = -13;

    @observable mapRangeMax = 13;

    @observable mapColors = [];

    @observable drawerHeightLarge = '100vh';

    @observable drawerHeightMediumLarge = '90vh';

    @observable drawerHeightMedium = '75vh';

    @observable drawerHeightMediumMedium = '60vh';

    @observable drawerHeightMediumSmall = '50vh';

    @observable drawerHeightSmall = '40vh';

    @observable drawerHeightXSmall = '20vh';

    @observable drawerHeight = '500px';

    @observable darkmode = util.get('darkmode');

    @observable viewmode = util.get('viewmode') || 'normal';

    @observable counter = util.get('counter');

    @observable language = util.get('language') || 'no';

    @observable languageList = [['en', 'English', '🇬🇧'], ['no', 'Norsk', '🇳🇴']];

    @observable mapChecked = util.get('mapChecked') || 'OpenStreetMap.Mapnik';

    @observable wantedPage = util.get('wantedPage') || '/';

    @observable showAdminFields = util.get('showAdminFields');

    @observable userEmail = util.getUserEmail();

    @observable userCellphone = util.getUserCellphone();

    @observable jwtToken = util.getJwtToken();

    @observable apiServer = util.getApiServer();

    @observable mapType = util.get('mapType') || 'topograatone';

    @observable imageDirection = util.get('imageDirection') || 'horizontal';

    @observable apiServerChanged = false;

    @observable webocketUrl = util.getWebsocketServer();

    @observable webocketUrlChanged = false;

    @observable fingerprint = '';

    @observable path = '/';

    @observable currentVersion = 0;

    @observable latestVersion = 0;

    @observable previousPath = '';

    @observable workoutType = 'all';

    @observable prevScroll = 0;

    @observable publicTrackFilter = '';

    @observable hasShareApi = false;

    @observable isCordova = false;

    @observable cordovaIsReady = false;

    @observable connectionStatus = 'unknown';

    @observable displayDetails = util.get('displayDetails') || {};

    @observable showInput = util.get('showInput') || {};

    @observable editUserDetails = {};

    @observable veterinaryUserDetails = {};

    @observable selectedClassIds = util.get('selectedClassIds') || [];

    @observable searchResultStories = [];

    @observable searchResultDogs = [];

    @observable searchResultWorkouts = [];

    @observable searchResultTeams = [];

    @observable searchResultTracks = [];

    @observable searchResultUsers = [];

    @observable publicTeams = [];

    @observable publicUsers = [];

    @observable currentCustomer = {};

    @observable swipeLeft = () => {};

    @observable swipeRight = () => {
        // Swipe to go back interferes with the swipe right inside the lists.
        // Be careful!
        //
        // console.log('path', this.path)
        // if (
        //     /^\/race\//.test(this.path)
        //     || /^\/raceclass\//.test(this.path)
        //     // || /^\/installations\//.test(this.path)
        // ) {
        //     window.history.back();
        // }
    };

    @observable swipeUp = () => {};

    @observable swipeDown = () => {};


    @observable swipeRights = [];

    @observable swipeLefts = [];

    @observable swipeUps = [];

    @observable swipeDowns = [];

    @observable swipeRightIndicate = () => {};

    @observable swipeLeftIndicate = () => {};

    @observable swipeUpIndicate = () => {};

    @observable swipeDownIndicate = () => {};

    @observable swipeRightIndicates = [];

    @observable swipeLeftIndicates = [];

    @observable swipeUpIndicates = [];

    @observable swipeDownIndicates = [];

    @observable showDashoard = false;

    @observable appContainer = null;

    @observable showDrawer1 = false;

    @observable showDrawer2 = false;

    @observable showDrawer3 = false;

    @observable showDrawer4 = false;

    @observable showDrawer5 = false;

    @observable showDrawer6 = false;

    @observable showDrawer7 = false;

    @observable showDrawer8 = false;

    @observable showDrawer9 = false;

    @observable showDrawerRight1 = false;

    @observable showDrawerRight2 = false;

    @observable showDrawerRight3 = false;

    @observable showDrawerRight4 = false;

    @observable showDrawerRight5 = false;

    @observable drawerComponent1 = null;

    @observable drawerComponent2 = null;

    @observable drawerComponent3 = null;

    @observable drawerComponent4 = null;

    @observable drawerComponent5 = null;

    @observable drawerComponent6 = null;

    @observable drawerComponent7 = null;

    @observable drawerComponent8 = null;

    @observable drawerComponent9 = null;

    @observable.ref drawerProps1 = {};

    @observable.ref drawerProps2 = {};

    @observable.ref drawerProps3 = {};

    @observable.ref drawerProps4 = {};

    @observable.ref drawerProps5 = {};

    @observable.ref drawerProps6 = {};

    @observable.ref drawerProps7 = {};

    @observable.ref drawerProps8 = {};

    @observable.ref drawerProps9 = {};

    // Top sheet (mirrors openDrawer; single level, persistent across nav)
    @observable showTopSheet = false;

    @observable topSheetComponent = null;

    @observable.ref topSheetProps = {};

    @observable topSheetHeight = '25vh';

    @observable logs = [];

    @observable errors = [];

    @observable totalErrors = 0;

    @observable errorsCurrentPage = 1;

    @observable errorsLimit = 50;

    @observable statistics = [];

    @observable apiLogs = [];

    @observable apiLogsUsers = [];

    @observable apiLogsCustomers = [];

    @observable apiLogRaw = null;

    @observable queryLogs = [];

    @observable queryLogsUsers = [];

    @observable queryLogsCustomers = [];

    @observable mainView = 'webcam';

    @observable subView = null;

    @observable weather = [];

    @observable loadMore = () => {};

    @observable mainViewCallback = () => {};

    @observable subViewCallback = () => {};

    @observable usageLog = [];

    usageLogCapWarned = false;

    @observable user = {};

    @observable clickOnLogo = () => {};

    // Registered by App.startLogTimer (its sendLogs); lets views flush usage events on demand.
    usageLogFlusher = null;

    // Generic logo-tap counter (B). Consumers such as C easter-egg triggers
    // react to changes; appState itself attaches no meaning to the count.
    @observable logoClickCount = 0;

    @action
    noteLogoClick() {
        this.logoClickCount += 1;
    }

    // Toast messages may be JSX vnodes (e.g. <Text id='...'>...</Text>), so this
    // array MUST stay shallow. With a deeply observable array, a pushed vnode is
    // converted into an observable object, and when Preact renders it (footer
    // renders {toast.message}) it writes its internal bookkeeping fields (__/__b/
    // __i) straight onto that object outside any MobX action — which throws the
    // MobX strict-mode invariant and crashes the whole app in production.
    @observable.shallow toasts = [];

    @action
    addToast(message, timeout = 5000) {
        // Add toast message to the toasts array.
        // This should be shown 5 seconds after it is added.
        this.toasts.push({
            timestamp: new Date(),
            ...message,
        });
        // Get index of the last toast message.
        setTimeout(() => {
            this.removeToast();
        }, timeout);

        // It should be possible to add more than one toast message at the same time.
        // If the toasts array is more than 20, remove the first one.
        if (this.toasts.length > 20) {
            this.removeToast();
        }
    }

    @action
    removeToast(index = 0) {
        this.toasts.splice(index, 1);
    }

    @action
    setClickOnLogo(func) {
        if (util.isFunction(func)) {
            this.clickOnLogo = func;
        }
    }

    /**
     * The App component owns the usage log delivery timer (sendLogs every 10 s). It registers
     * its sender here so views that need fresh numbers (stats drawers) can flush on demand.
     */
    @action
    setUsageLogFlusher(func) {
        if (util.isFunction(func)) {
            this.usageLogFlusher = func;
        }
    }

    async flushUsageLog() {
        const flusher = this.usageLogFlusher;
        if (!util.isFunction(flusher)) {
            return;
        }
        try {
            await flusher();
        } catch (err) {
            // The App timer retries delivery; stats just load without the newest events.
        }
    }

    @action
    addUsageLog(data) {
        const inputData = JSON.parse(JSON.stringify(data));
        if (inputData?.props) {
            const keys = Object.keys(inputData.props);
            for (let i = 0; i < keys.length; i++) {
                if (util.isObject(inputData.props[keys[i]])) {
                    delete inputData.props[keys[i]];
                }
            }
        }
        this.usageLog.push({
            domain: window.location.hostname,
            ...inputData,
        });
        while (this.usageLog.length > 500) {
            const nonFeedIndex = this.usageLog.findIndex(event => !String(event?.type || '').startsWith('feed_'));
            this.usageLog.splice(nonFeedIndex >= 0 ? nonFeedIndex : 0, 1);
            if (!this.usageLogCapWarned) {
                console.warn('Usage log queue reached 500 events; oldest diagnostics are being dropped.');
                this.usageLogCapWarned = true;
            }
        }
    }

    @action
    removeUsageLogPrefix(length) {
        const count = Math.max(0, Math.min(Number(length) || 0, this.usageLog.length));
        if (count > 0) {
            this.usageLog.splice(0, count);
        }
    }

    @action
    emptyUsageLog() {
        this.usageLog = [];
    }

    // ============================================
    // Enhanced Usage Tracking Methods
    // ============================================

    /**
     * Track article engagement events
     * @param {string} eventType - 'view', 'read_complete', 'share', 'like', 'comment'
     * @param {object} article - Article object with id, title, category
     * @param {object} extra - Additional data like readTimeSeconds, shareMethod
     */
    @action
    trackArticle(eventType, article, extra = {}) {
        this.addUsageLog({
            type: `article_${eventType}`,
            props: {
                articleId: article?.id,
                title: article?.title,
                category: article?.category,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track product engagement events (product view drawer, cart)
     * @param {string} eventType - 'view', 'add_to_cart'
     * @param {object} product - Product object with id, name, productNumber, customer
     * @param {object} extra - Additional data like color, size, race
     */
    @action
    trackProduct(eventType, product, extra = {}) {
        this.addUsageLog({
            type: `product_${eventType}`,
            props: {
                productId: product?.id,
                name: product?.name,
                productNumber: product?.productNumber,
                customer: product?.customer,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track race engagement events
     * @param {string} eventType - 'view', 'live_tracking', 'result_view', 'follow'
     * @param {object} race - Race object with id, name
     * @param {object} extra - Additional data like duration, classId
     */
    @action
    trackRace(eventType, race, extra = {}) {
        this.addUsageLog({
            type: `race_${eventType}`,
            props: {
                raceId: race?.id,
                raceName: race?.name || race?.title,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track journal/veterinary events
     * @param {string} eventType - 'create', 'view', 'update', 'close', 'print'
     * @param {object} journal - Journal object
     * @param {object} extra - Additional data
     */
    @action
    trackJournal(eventType, journal, extra = {}) {
        this.addUsageLog({
            type: `journal_${eventType}`,
            props: {
                journalId: journal?.id,
                animalId: journal?.animal,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track sale events
     * @param {string} eventType - 'create', 'complete', 'payment', 'cancel'
     * @param {object} sale - Sale object
     * @param {object} extra - Additional data like amount, paymentMethod
     */
    @action
    trackSale(eventType, sale, extra = {}) {
        this.addUsageLog({
            type: `sale_${eventType}`,
            props: {
                saleId: sale?.id,
                saleNumber: sale?.saleNumber,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track feature usage
     * @param {string} feature - Main feature name
     * @param {string} action - Specific action within feature
     * @param {object} extra - Additional data
     */
    @action
    trackFeature(feature, action, extra = {}) {
        this.addUsageLog({
            type: 'feature_use',
            props: {
                feature,
                action,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track user session events
     * @param {string} eventType - 'start', 'end', 'active'
     * @param {object} extra - Duration, pageViews, etc.
     */
    @action
    trackSession(eventType, extra = {}) {
        this.addUsageLog({
            type: `session_${eventType}`,
            props: extra,
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track search events
     * @param {string} searchType - 'global', 'article', 'product', 'animal', 'visitor'
     * @param {string} query - Search query
     * @param {number} resultCount - Number of results
     */
    @action
    trackSearch(searchType, query, resultCount = 0) {
        this.addUsageLog({
            type: 'search',
            props: {
                searchType,
                query,
                resultCount,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track calendar/booking events
     * @param {string} eventType - 'view', 'create', 'update', 'cancel', 'confirm'
     * @param {object} event - Calendar event object
     * @param {object} extra - Additional data
     */
    @action
    trackCalendar(eventType, event, extra = {}) {
        this.addUsageLog({
            type: `calendar_${eventType}`,
            props: {
                eventId: event?.id,
                calendarId: event?.calendar,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track race class signup events
     * @param {string} eventType - 'create', 'update', 'cancel', 'payment'
     * @param {object} contestant - Race class contestant object
     * @param {object} extra - Additional properties
     */
    @action
    trackRaceClassSignup(eventType, contestant, extra = {}) {
        this.addUsageLog({
            type: `raceClassSignup_${eventType}`,
            props: {
                contestantId: contestant?.id,
                raceClassId: contestant?.raceClass,
                raceId: contestant?.race,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track race class view events
     * @param {string} eventType - 'view', 'result_view', 'live_tracking'
     * @param {object} raceClass - Race class object
     * @param {object} extra - Additional properties
     */
    @action
    trackRaceClass(eventType, raceClass, extra = {}) {
        this.addUsageLog({
            type: `raceClass_${eventType}`,
            props: {
                raceClassId: raceClass?.id,
                raceClassName: raceClass?.title || raceClass?.name,
                raceId: raceClass?.race,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track convention events
     * @param {string} eventType - 'view', 'signup', 'result_entered', 'result_view'
     * @param {object} convention - Convention object
     * @param {object} extra - Additional properties
     */
    @action
    trackConvention(eventType, convention, extra = {}) {
        this.addUsageLog({
            type: `convention_${eventType}`,
            props: {
                conventionId: convention?.id,
                conventionTitle: convention?.title,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    /**
     * Track stream engagement events
     * @param {string} eventType - 'view', 'play', 'pause', 'ended', 'seek', 'fullscreen'
     * @param {object} stream - Stream object with id, name, race
     * @param {object} extra - Additional data like duration, currentTime, quality
     */
    @action
    trackStream(eventType, stream, extra = {}) {
        this.addUsageLog({
            type: `stream_${eventType}`,
            props: {
                streamId: stream?.id,
                streamName: stream?.name,
                raceId: stream?.race,
                streamUrl: stream?.streamUrl,
                ...extra,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
    }

    // ============================================
    // End Enhanced Usage Tracking Methods
    // ============================================

    @action
    setUser(user) {
        this.user = user;
    }

    @action
    updateKeyValue(key, value) {
        this[key] = value;
    }

    @action
    updateObjectKeyValue(obj, key, value) {
        this[obj][key] = value;
    }

    @action
    setPageHeaders(headers) {
        // replace html headers for og and twitter
        {/* <meta property="og:url" content="https://litt.no/Livesenter/Live/322" />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="no_NO" />
        <meta property="og:title" content="Live" />
        <meta property="og:description" content="" />
        <meta property="og:site_name" content="litt.no" />
        <meta property="og:image" content="https://litt.no/pho/photo/simpleblog-77e5d472-9848-4bf1-a43d-f82d9783206a.jpg?w=600" />
        <meta property="og:image:width" content="600" />
        <meta property="og:image:type" content="image/jpeg" />

        <!-- Twitter Card -->
        <meta name="twitter:site" content="https://twitter.com/sorenso" />
        <meta name="twitter:text:title" content="Live" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:description" content="" />
        <meta name="twitter:creator" content="https://twitter.com/sorenso" />
        <meta name="twitter:image" content="https://litt.no/pho/photo/simpleblog-77e5d472-9848-4bf1-a43d-f82d9783206a.jpg?w=600" />

        <meta name="thumbnail" content="https://litt.no/pho/photo/simpleblog-77e5d472-9848-4bf1-a43d-f82d9783206a.jpg?w=600" /> */}

        if (headers) {
            document.title = headers.title;
            if (document.querySelector('meta[name="title"]')) {
                document.querySelector('meta[name="title"]').setAttribute("content", headers.title);
            } else {
                addMetaTag('title', headers.title);
            }
            if (document.querySelector('meta[name="description"]')) {
                document.querySelector('meta[name="description"]').setAttribute("content", headers.description);
            } else {
                addMetaTag('description', headers.description);
            }
            if (document.querySelector('meta[name="thumbnail"]')) {
                document.querySelector('meta[name="thumbnail"]').setAttribute("content", headers.image);
            } else {
                addMetaTag('thumbnail', headers.image);
            }

            if (document.querySelector('meta[property="og:url"]')) {
                document.querySelector('meta[property="og:url"]').setAttribute("content", headers.url);
            } else {
                addMetaTag('og:ur', headers.url);
            }
            if (document.querySelector('meta[property="og:title"]')) {
                document.querySelector('meta[property="og:title"]').setAttribute("content", headers.title);
            } else {
                addMetaTag('og:title', headers.title);
            }
            if (document.querySelector('meta[property="og:description"]')) {
                document.querySelector('meta[property="og:description"]').setAttribute("content", headers.description);
            } else {
                addMetaTag('og:description', headers.description);
            }
            if (document.querySelector('meta[property="og:image"]')) {
                document.querySelector('meta[property="og:image"]').setAttribute("content", headers.image);
            } else {
                addMetaTag('og:image', headers.image);
            }

            if (document.querySelector('meta[property="twitter:text:title"]')) {
                document.querySelector('meta[property="twitter:text:title"]').setAttribute("content", headers.title);
            } else {
                addMetaTag('twitter:text:title', headers.title);
            }
            if (document.querySelector('meta[property="twitter:description"]')) {
                document.querySelector('meta[property="twitter:description"]').setAttribute("content", headers.description);
            } else {
                addMetaTag('twitter:description', headers.description);
            }
            if (document.querySelector('meta[property="twitter:image"]')) {
                document.querySelector('meta[property="twitter:image"]').setAttribute("content", headers.image);
            } else {
                addMetaTag('twitter:image', headers.image);
            }
        }
    }

    updateMetaTags(object) {
        updateMetaTags(object);
    }

    storeDefaultMetaTags({ title = document.title, keywords = '', description = '', image = '', url = '', type = '' }) {
        // Store the default meta tags
        this.defaultMetaTags = {
            title,
            keywords,
            description,
            image,
            url,
            image,
            type,
        };
    }

    restoreDefaultMetaTags() {
        // Restore the default meta tags
        updateMetaTags(this.defaultMetaTags);
    }

    @action
    setWebsocketServer(webocketUrl) {
        util.setWebsocketServer(webocketUrl);
        this.webocketUrl = webocketUrl;
        this.webocketUrlChanged = true;
    }

    @action
    setLoadMore(func) {
        if (util.isFunction(func)) {
            this.loadMore = func;
        }
    }

    @action
    setState(state) {
        this.state = state;
    }
    @action
    setInputProps(inputProps) {
        this.inputProps = inputProps;
        this.opts = {
            ...this.opts,
            ...inputProps,
        };
    }

    @action
    toggleDrawer(state, drawerLevel = 1) {
        // console.log('toggleDrawer', { state, drawerLevel });
        if (drawerLevel) {
            this[`showDrawer${drawerLevel}`] = false;
            if (drawerLevel === 1) {
                for (let i = 2; i <= 9; i++) {
                    this[`showDrawer${i}`] = false;
                }
                util.toggleBodyClasses('noscroll', false);
            }
            return;
        }
    }

    getTopMostDrawer() {
        for (let i = 9; i >= 1; i--) {
            if (this[`showDrawer${i}`]) {
                return i;
            }
        }
        return 0;
    }

    @action
    closeDrawer(state, drawerLevel = 1) {
        this.toggleDrawer(state, drawerLevel);
    }

    /**
     * Opens a drawer with the specified component and props.
     * @param {React.ComponentType} component - The component to render in the drawer.
     * @param {Object} props - The props to pass to the component.
     * @param {number} [drawerLevel=1] - The level of the drawer to open (1 or 2).
     * @returns {void}
     */
    @action
    openDrawer(component, props, drawerLevel = 1) {
        if (drawerLevel) {
            this.addUsageLog({
                type: 'openDrawer',
                url: component,
                timestamp: Math.floor(new Date().getTime() / 1000),
                browser: getBrowserInfo(),
                user: this.user ? this.user.id : null,
                drawerLevel,
                objectId: props?.object?.id,
                field: props?.field,
            });

            this[`drawerComponent${drawerLevel}`] = component;
            this[`drawerProps${drawerLevel}`] = props;
            this[`showDrawer${drawerLevel}`] = true;

            if (drawerLevel === 1) {
                util.toggleBodyClasses('noscroll', true);
            }
            return;
        }
    }

    @action
    toggleDrawerRight(state, drawerLevel = 1) {
        if (drawerLevel) {
            this[`showDrawerRight${drawerLevel}`] = false;
            if (drawerLevel === 1) {
                for (let i = 2; i <= 5; i++) {
                    this[`showDrawerRight${i}`] = false;
                }
                util.toggleBodyClasses('noscroll', false);
            }
            return;
        }
    }

    /**
     * Opens a right-hand drawer with the specified component and props.
     * @param {React.ComponentType} component - The component to render in the drawer.
     * @param {Object} props - The props to pass to the component.
     * @param {number} [drawerLevel=1] - The level of the drawer to open (1 or 2).
     * @returns {void}
     */
    @action
    openDrawerRight(component, props, drawerLevel = 1) {
        // console.log('openDrawer', component, props, drawerLevel)
        this.addUsageLog({
            type: 'openDrawerRight',
            url: component,
            timestamp: Math.floor(new Date().getTime() / 1000),
            browser: getBrowserInfo(),
            user: this.user ? this.user.id : null,
            drawerLevel,
            objectId: props?.object?.id,
            field: props?.field,
        });
        if (drawerLevel) {
            this[`drawerComponent${drawerLevel}`] = component;
            this[`drawerProps${drawerLevel}`] = props;
            this[`showDrawerRight${drawerLevel}`] = true;
            if (drawerLevel === 1) {
                util.toggleBodyClasses('noscroll', true);
            }
            return;
        }
    }

    @action
    toggleTopSheet() {
        this.showTopSheet = !this.showTopSheet;
    }

    @action
    closeTopSheet() {
        this.showTopSheet = false;
    }

    /**
     * Opens the global top sheet with the specified component and props.
     * Single level only (replaces any existing top sheet).
     * @param {string|React.ComponentType} component - Component identifier or reference rendered in the sheet.
     * @param {Object} props - Props passed through to the rendered component.
     * @param {Object} [opts] - { height } override for sheet height (default '25vh').
     */
    @action
    openTopSheet(component, props = {}, opts = {}) {
        const { height = '25vh' } = opts;
        this.addUsageLog({
            type: 'openTopSheet',
            url: typeof component === 'string' ? component : (component?.name || 'unknown'),
            timestamp: Math.floor(new Date().getTime() / 1000),
            browser: getBrowserInfo(),
            user: this.user ? this.user.id : null,
            objectId: props?.object?.id,
            field: props?.field,
        });
        this.topSheetComponent = component;
        this.topSheetProps = props;
        this.topSheetHeight = height;
        this.showTopSheet = true;
    }

    @action
    setAppContainer(ref) {
        this.appContainer = ref;
    }

    setFocusOnAppContainer = () => {
        if (this.appContainer) {
            this.appContainer.focus();
        }
    }

    appContainerScrollTop = (top = 0, left = 0) => {
        // console.log(this.appContainer.clientHeight, this.appContainer.scrollLeft, this.appContainer.scrollTop);
        // console.log('appContainerScrollTop', this.appContainer, top, left);
        scrollTo(this.appContainer, top, left);
        // console.log(this.appContainer.clientHeight, this.appContainer.scrollLeft, this.appContainer.scrollTop);
    }

    @action
    toggleDashboard() {
        this.showDashoard = !this.showDashoard;
    }

    @action
    setSwipeLeft(func) {
        if (typeof func === 'function') {
            this.swipeLefts.push(this.swipeLeft);
            this.swipeLeft = func;
        } else {
            this.swipeLeft = this.swipeLefts.pop();
        }
    }

    @action
    setSwipeRight(func) {
        if (typeof func === 'function') {
            this.swipeRights.push(this.swipeRight);
            this.swipeRight = func;
        } else {
            this.swipeRight = this.swipeRights.pop();
        }
    }

    @action
    setSwipeUp(func) {
        if (typeof func === 'function') {
            this.swipeUps.push(this.swipeUp);
            this.swipeUp = func;
        } else {
            this.swipeUp = this.swipeUps.pop();
        }
    }

    @action
    setSwipeDown(func) {
        if (typeof func === 'function') {
            this.swipeDowns.push(this.swipeDown);
            this.swipeDown = func;
        } else {
            this.swipeDown = this.swipeDowns.pop();
        }
    }

    @action
    setSwipeRightIndicate(func) {
        if (typeof func === 'function') {
            this.swipeRightIndicates.push(this.swipeRightIndicate);
            this.swipeRightIndicate = func;
        } else {
            this.swipeRightIndicate = this.swipeRightIndicates.pop();
        }
    }

    @action
    setSwipeLeftIndicate(func) {
        if (typeof func === 'function') {
            this.swipeLeftIndicates.push(this.swipeLeftIndicate);
            this.swipeLeftIndicate = func;
        } else {
            this.swipeLeftIndicate = this.swipeLeftIndicates.pop();
        }
    }

    @action
    setSwipeUpIndicate(func) {
        if (typeof func === 'function') {
            this.swipeUpIndicates.push(this.swipeUpIndicate);
            this.swipeUpIndicate = func;
        } else {
            this.swipeUpIndicate = this.swipeUpIndicates.pop();
        }
    }

    @action
    setSwipeDownIndicate(func) {
        if (typeof func === 'function') {
            this.swipeDownIndicates.push(this.swipeDownIndicate);
            this.swipeDownIndicate = func;
        } else {
            this.swipeDownIndicate = this.swipeDownIndicates.pop();
        }
    }

    @action
    setMapType(type) {
        this.mapType = type;
        util.set('mapType', type);
    }

    @action
    setImageDirection(imageDirection) {
        this.imageDirection = imageDirection;
        util.set('imageDirection', imageDirection);
    }

    @action
    setApiServer(apiServer) {
        util.setApiServer(apiServer);
        this.apiServer = apiServer;
        this.apiServerChanged = true;
    }

    @action
    localUpdateField(key, value) {
        this[key] = value;
    }

    @action
    setSelectedClassId(classId) {
        // console.log('addSelectedClassId', classId);
        if (classId) {
            this.selectedClassIds = [classId];
            util.set('selectedClassIds', this.selectedClassIds);
        }
    }

    @action
    addSelectedClassId(classId) {
        // console.log('addSelectedClassId', classId);
        if (classId) {
            const idx = this.selectedClassIds?.indexOf(classId);
            if (idx === -1) {
                this.selectedClassIds.push(classId);
            }
            util.set('selectedClassIds', this.selectedClassIds);
        }
    }

    @action
    removeSelectedClassId(classId) {
        if (classId) {
            const idx = this.selectedClassIds?.indexOf(classId);
            if (idx > -1) {
                this.selectedClassIds.splice(idx, 1);
            }
            this.selectedClassIds = this.selectedClassIds.filter(e => e !== null);
            util.set('selectedClassIds', this.selectedClassIds);
        }
    }

    @action
    hasSelectedClassId(classId) {
        const idx = this.selectedClassIds?.indexOf(classId);
        if (idx > -1) {
            return true;
        }
        return false;
    }

    @action
    toggleSelectedClassId(classId, single) {
        // console.log('toggleSelectedClassId', classId, this.selectedClassIds);
        if (single) {
            this.setSelectedClassId(classId);
        } else if (this.hasSelectedClassId(classId)) {
            this.removeSelectedClassId(classId);
        } else {
            this.addSelectedClassId(classId);
        }
    }

    @action
    toggleDisplayDetails(email) {
        this.displayDetails[email] = !this.displayDetails[email];
        util.set('displayDetails', this.displayDetails);
    }

    @action
    toggleInput(type) {
        this.showInput[type] = !this.showInput[type];
        util.set('showInput', this.showInput);
    }

    @action
    toggleEditUserDetails(email) {
        this.editUserDetails[email] = !this.editUserDetails[email];
    }

    @action
    toggleVeterinaryUserDetails(email, onlyOne) {
        if (onlyOne) {
            const prevValue = this.veterinaryUserDetails[email];
            this.veterinaryUserDetails = {};
            this.veterinaryUserDetails[email] = !prevValue;
        } else {
            this.veterinaryUserDetails[email] = !this.veterinaryUserDetails[email];
        }
    }

    @action
    setLanguage(language) {
        this.language = language;
        util.set('language', language);
        window.location.reload(true);
    }

    @action
    setWantedPage(wantedPage) {
        this.wantedPage = wantedPage;
        util.set('wantedPage', wantedPage);
    }

    @action
    setShowAdminFields(showAdminFields) {
        this.showAdminFields = showAdminFields;
        util.set('showAdminFields', showAdminFields);
    }

    @action
    setMapChecked(mapChecked) {
        this.mapChecked = mapChecked;
        util.set('mapChecked', mapChecked);
    }

    @action
    setPath(path) {
        this.path = path;
    }

    @action
    setVersion(version = {}) {
        this.currentVersion = version.build;
    }

    @action
    setLatestVersion(version = {}) {
        this.latestVersion = version.build;
    }

    @action
    setPrevPath(path) {
        this.previousPath = path;
    }
    @action
    setPrevScroll(scroll) {
        this.prevScroll = scroll;
    }

    @action
    setMousePosition(pos = []) {
        this.mousePos = pos;
    }

    @action
    setView(view) {
        this.view = view;
        util.set('view', view);
    }

    @action
    setWorkoutType(type) {
        this.workoutType = type;
    }

    @action
    setViewKey(key, value) {
        if (!util.isObject(this.view)) {
            this.view = {};
        }
        this.view[key] = value;
        util.setObject('view', key, value);
    }

    @action
    setKey(key, value) {
        this[key] = value;
    }

    @action
    checkShareApi() {
        if (navigator && navigator.share) {
            // Web Share is supported
            this.hasShareApi = true;
        }
    }

    async shareUrl({ url, title, text }) {
        try {
            await navigator.share({ url, title, text });
            return true;
        } catch (e) {
            console.error('Could not share!', e);
            return false;
        }
    }

    @action
    checkCordova() {
        // if(!window.cordova && !window.BackgroundGeolocation) {
        if(!window.cordova) {
            this.isCordova = false;
            // document.body.style['margin-top'] = '200px';
        } else {
            this.isCordova = true;
            // document.body.style['margin-top'] = '200px';
        }
    }

    @action
    cordovaSetToReady() {
        this.cordovaIsReady = true;
    }

    @action
    setConnectionStatus(status) {
        this.connectionStatus = status;
    }

    @action
    incCounter() {
        this.counter += 1;
        util.set('counter', this.counter);
    }

    @action
    decCounter() {
        this.counter -= 1;
        util.set('counter', this.counter);
    }

    @computed
    get counterTimes2() {
        return this.counter * 2;
    }

    @action
    toggleDarkmode(value) {
        // console.log('toggleDarkmode', this.darkmode);
        if (isDarkmodeForced(this.currentCustomer)) {
            // Domain-forced dark mode: login pages and logout must not flip it back.
            this.darkmode = true;
        } else if (util.isDefined(value)) {
            this.darkmode = value;
        } else {
            this.darkmode = !this.darkmode;
        }
        util.set('darkmode', this.darkmode);
        localUtil.toggleBootstrap53DarkMode(this.darkmode);
        syncNativeStatusBar({ darkmode: this.darkmode });
    }

    @action
    toggleViewmode(mode) {
        // console.log('toggleViewmode', this.viewmode);
        this.viewmode = mode || !this.viewmode;
        util.set('viewmode', this.viewmode);
    }

    @action
    toggleField(key, val) {
        this[key] = val ? val : !this.val;
    }

    @action
    setLatlng(latlng = []) {
        this.latlng = latlng;
    }

    @action
    setMapColorMode(colorMode) {
        this.mapColorMode = colorMode;
    }

    @action
    setMapRange(range, min, max) {
        this.mapRange = range;
        this.mapRangeMin = min;
        this.mapRangeMax = max;
    }

    @action
    setMapColors(colors) {
        this.mapColors = colors;
    }

    @action
    updateField(key, val) {
        this[key] = val;
    }

    @action
    findPublicTeams(teams = []) {
        const results = this.publicTeams.filter(e => teams?.indexOf(e.id) > -1 && e.public).map(e => toJS(e));
        return results;
    }

    @action
    findPublicTeam(team) {
        const idx = this.publicTeams?.findIndex(e => e.id === team);
        if (idx > -1) {
            return this.publicTeams[idx];
        }
    }

    @action
    findPublicTeamByUuidv4(team) {
        const idx = this.publicTeams?.findIndex(e => e.uuidv4 === team);
        if (idx > -1) {
            return this.publicTeams[idx];
        }
    }

    @action
    findPublicTeamByMembers(user) {
        if (!user) {
            return [];
        }
        const teams = this.publicTeams.filter(e => e.members?.indexOf(user) > -1);
        return teams;
    }

    @action
    findPublicUser(user) {
        if (!this.publicUsers) {
            return {};
        }
        const idx = this.publicUsers?.findIndex(e => e.id === user);
        if (idx > -1) {
            return this.publicUsers[idx];
        }
    }

    @action
    setMainView(view) {
        this.mainView = view;
        this.mainViewCallback(view);
    }

    @action
    setSubView(view) {
        this.subView = view;
        this.subViewCallback(view);
    }

    @action
    setMainViewCallback(func) {
        this.mainViewCallback = func;
    }

    @action
    setSubViewCallback(func) {
        this.subViewCallback = func;
    }

    async getCustomerByHostname() {
        const res = await util.fetchApi('/api/customers/public', { publish: false }, { hostname: window.location.hostname });
        if (res.status === 200) {
            this.updateField('currentCustomer', res.data?.customer);
        }
    }

    async getFingerprint() {
        const res = await util.fetchApi('/api/public/fingerprint/', { publish: false });
        if (res.status === 200) {
            this.updateField('fingerprint', res.fingerprint);
            this.updateField('jwtToken', res.jwtToken);
        }
    }

    async getInfo() {
        const res = await util.fetchApi('/api/info', { publish: false, credentials: 'include' });
        if (res.status === 200) {
            this.updateField('isAdmin', res.data.isAdmin);
            this.updateField('isExpert', res.data.isExpert);
            this.updateField('currentEmail', res.data.currentEmail);
            this.updateField('jwtToken', res.data.jwtToken);
            util.setJwtToken(res.data.jwtToken);
            return true;
        } else {
            return false;
        }
    }

    async sendEmail({ from, to = [], subject = '', body = '', bodyText = '', format = 'markdown', raceId, raceClassId, attachmentUrls = [], emailType = 'transactional' }) {
        const response = await util.fetchApi(`/api/email/`, { publish: true, method: 'POST' }, { from, to, subject, body, bodyText, format, raceId, raceClassId, attachmentUrls, emailType });
        switch (response.status) {
            case 200:
                return response;
            case 401:
                route('/');
                break;
            default:
                return response;
        }
    }

    async sendSms({ from = 'RaskePoter', to = [], body = '' }) {
        const response = await util.fetchApi(`/api/sms/`, { publish: true, method: 'POST' }, { from, to, body });
        switch (response.status) {
            case 200:
                return response;
            case 401:
                route('/');
                break;
            default:
                return response;
        }
    }

    async sendEmailPlain({ to = [], subject = '', body = '', from, attachmentUrls = [], emailType = 'transactional' }) {
        const response = await util.fetchApi(`/api/email/plain`, { publish: true, method: 'POST' }, { from, to, subject, body, attachmentUrls, emailType });
        switch (response.status) {
            case 200:
                return response;
            case 401:
                route('/');
                break;
            default:
                return response;
        }
    }

    @action
    setWeather = (weather) => {
        this.weather = weather;
    }

    async postLog({ type = 'appPerformance', deviceInfo, currentLocation }) {
        const lines = this.getLogs();
        const response = await util.fetchApi(`/api/logs/`, { publish: false, method: 'POST' }, {
            lines,
            deviceInfo,
            currentLocation,
            type,
        });
        switch (response.status) {
            case 201:
                return response;
            case 401:
                route('/');
                break;
        }
    }

    async getWeather({ lat, lon, altitude, updateAppstate = true }) {
        const response = await util.fetchApi(`/api/yr/`, { publish: false, method: 'GET' }, {
            lat, lon, altitude,
        });
        switch (response.status) {
            case 200:
                if (updateAppstate) {
                    this.setWeather(response.data);
                }
                return response.data;
            case 401:
                route('/');
                break;
        }
    }

    onPhotoURISuccess = (imageURI) => {
        console.log(JSON.stringify(imageURI));
    }

    onFail = (message) => {
        console.log('Failed because: ' + message);
    }

    uploadPhoto = (imageURI) => {
        const options = new FileUploadOptions();
        options.fileKey = 'file';
        options.fileName = imageURI.substr(imageURI.lastIndexOf('/') + 1) + '.jpg';
        options.mimeType = 'text/plain';

        var params = new Object();
        options.params = params;

        var ft = new FileTransfer();
        ft.upload(imageURI, encodeURI("http://some.server.com/upload.php"), win, fail, options);
    }

    win(r) {
        console.log("Code = " + r.responseCode);
        console.log("Response = " + r.response);
        console.log("Sent = " + r.bytesSent);
    }

    fail(error) {
        void appAlert("An error has occurred: Code = " + error.code);
        console.log("upload error source " + error.source);
        console.log("upload error target " + error.target);
    }

    getPhoto(source = Camera?.PictureSourceType?.PHOTOLIBRARY) {
        if (navigator.camera) {
            navigator.camera.getPicture(this.onPhotoURISuccess, this.onFail, {
                quality: 50,
                mediaType: Camera?.MediaType?.PICTURE,
                destinationType: Camera?.DestinationType?.FILE_URI,
                sourceType: source,
                correctOrientation: true,
                saveToPhotoAlbum: true,
                cameraDirection: Camera?.Direction?.BACK,
            });
        }
    }

    async spiderPage({ url }) {
        const response = await util.fetchApi(`/api/spider/${encodeURIComponent(url)}`, {}, this);
        switch (response.status) {
            case 200:
                return {
                    ...response.data,
                }
                break;
            default:
                return response;
        }
    }

    async loadErrors({ limit = 50, offset = 0, sort = '-createdDate', search = '', cutoffDate = '', cutoffDateEnd = '' }) {
        const params = { limit, offset, sort };
        if (search) {
            params.search = search;
        }
        if (cutoffDate) {
            params.cutoffDate = cutoffDate;
        }
        if (cutoffDateEnd) {
            params.cutoffDateEnd = cutoffDateEnd;
        }
        const response = await util.fetchApi(`/api/admin/errors/`, { publish: false, method: 'GET' }, params);
        switch (response.status) {
            case 200:
                this.updateKeyValue('errors', response.data);
                this.updateKeyValue('totalErrors', response.total || 0);
        }
    }

    async loadStatistics({ fromDate, toDate } = {}) {
        const params = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        const response = await util.fetchApi(`/api/usageLogs/statistics`, { publish: false, method: 'GET' }, params);
        switch (response.status) {
            case 200:
                this.updateKeyValue('statistics', response.data);
        }
    }

    async loadApiLogs({ search, limit = 500, offset = 0, userId, customer, fromDate, toDate, sanitized } = {}) {
        const params = { limit, offset };
        if (search) params.search = search;
        if (userId) params.userId = userId;
        if (customer) params.customer = customer;
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (sanitized) params.sanitized = 1;
        const response = await util.fetchApi(`/api/apilogger/admin/`, { publish: false, method: 'GET' }, params);
        switch (response.status) {
            case 200:
                this.updateKeyValue('apiLogs', response.data);
                this.updateKeyValue('apiLogsUsers', response.included.users);
                this.updateKeyValue('apiLogsCustomers', response.included.customers);
        }
    }

    async loadApiLogRaw(id) {
        // Reset first so a stale row from a previous click can't be
        // misattributed to this one while the fetch is in flight.
        this.updateKeyValue('apiLogRaw', null);
        // offset=0 is sent so the endpoint's default 2-day window does not
        // hide older rows when fetching a single raw row by id.
        const response = await util.fetchApi(`/api/apilogger/admin/`, { publish: false, method: 'GET' }, {
            limit: 1,
            offset: 0,
            filter: JSON.stringify({ id }),
        });
        if (response.status === 200) {
            this.updateKeyValue('apiLogRaw', (response.data && response.data[0]) || null);
        }
    }

    async loadQueryLogs({ search, limit = 500, offset = 0 }) {
        const response = await util.fetchApi(`/api/querylogger/admin/`, { publish: false, method: 'GET' }, {
            limit, offset,
            search,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('queryLogs', response.data);
                this.updateKeyValue('queryLogsUsers', response.included.users);
                this.updateKeyValue('queryLogsCustomers', response.included.customers);
        }
    }

    async checkVersion() {
        const response = await util.fetchApi(`/api/version`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.setLatestVersion(response.data);
        }
    }

    async loadVideoStreamingUrl(streamName) {
        const response = await util.fetchApi(`/api/video/${streamName}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                console.log('loadVideoStreamingUrl', response.data);
                return response.data?.url?.url;
        }
    }

    async postnummerLookup(postnummer) {
        const response = await util.fetchApi(`/api/postnummer/${postnummer}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                console.log('postnummerLookup', response.data);
                return response.data?.url?.url;
        }
    }

    async animaliaIndividualList(individual = '') {
        const response = await util.fetchApi(`/api/integrations/animalia/individuals/${individual}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                console.log('animaliaIndividualList', response);
                return response;
        }
    }
    async animaliaDiagnosesList({ code = '', doImport = false }) {
        const response = await util.fetchApi(`/api/integrations/animalia/diagnoses/${code}`, { method: 'GET' }, {
            doImport,
        });
        switch (response.status) {
            case 200:
                console.log('animaliaDiagnosesList', response);
                return response;
        }
    }
    async animaliaMedicinesList({ doImport = false }) {
        const response = await util.fetchApi(`/api/integrations/animalia/medicines`, { method: 'GET' }, {
            doImport,
        });
        switch (response.status) {
            case 200:
                console.log('animaliaMedicinesList', response);
                return response;
        }
    }

    async festSyncPrices() {
        const response = await util.fetchApi('/api/integrations/fest/sync-prices', { method: 'GET' });
        return response;
    }

    async animaliaUploadVisit(visit, journal) {
        const response = await util.fetchApi(`/api/integrations/animalia/visits`, { method: 'POST' }, {
            visit,
            journal,
        });
        switch (response.status) {
            case 200:
                console.log('animaliaUploadVisit', response);
                return response;
            default:
                // console.log('animaliaUploadVisit', response);
                return response;
        }
    }

    async animaliaMkiPreview(producerNumber, { fraDato, dyreId, animalId }) {
        const response = await util.fetchApi(`/api/integrations/animalia/mki/${producerNumber}`, { method: 'GET' }, {
            fraDato,
            dyreId,
            animalId,
        });
        return response;
    }

    async animaliaMkiImport(animalId, events) {
        const response = await util.fetchApi('/api/integrations/animalia/healthrecords', { method: 'POST' }, {
            animalId,
            events,
        });
        return response;
    }

    async rubicGetClubs() {
        const response = await util.fetchApi(`/api/integrations/rubic/clubs`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                // console.log('rubicGetClubs', response);
                return response;
            default:
                // // console.log('rubicGetClubs', response);
                return response;
        }
    }

    async rubicGetClubById({ rubicOrganizationId }) {
        const response = await util.fetchApi(`/api/integrations/rubic/clubs`, { method: 'GET' }, {
            rubicOrganizationId,
        });
        switch (response.status) {
            case 200:
                // console.log('rubicGetClubById', response);
                return response;
            default:
                // // console.log('rubicGetClubById', response);
                return response;
        }
    }

    async rubicGetPersonSearch({ name, cellphone, email, birthDate }) {
        const response = await util.fetchApi(`/api/integrations/rubic/person-search`, { method: 'GET' }, {
            name,
            cellphone,
            email,
            birthDate
        });
        switch (response.status) {
            case 200:
                console.log('rubicGetPersonSearch (200)', response);
                return response;
            default:
                console.log('rubicGetPersonSearch (default)', response);
                return response;
        }
    }

    async rubicGetPersonById(personId) {
        const response = await util.fetchApi(`/api/integrations/rubic/person`, { method: 'GET' }, {
            personId,
        });
        switch (response.status) {
            case 200:
                // console.log('rubicGetPersonById', response);
                return response;
            default:
                // // console.log('rubicGetPersonById', response);
                return response;
        }
    }

    async rubicGetCompetitions(organizationId) {
        const response = await util.fetchApi(`/api/integrations/rubic/competitions`, { method: 'GET' }, {
            organizationId,
        });
        switch (response.status) {
            case 200:
                // console.log('rubicGetCompetitions', response);
                return response;
            default:
                // // console.log('rubicGetCompetitions', response);
                return response;
        }
    }

    async rubicGetCompetitionById(competitionId) {
        const response = await util.fetchApi(`/api/integrations/rubic/competition`, { method: 'GET' }, {
            competitionId,
        });
        switch (response.status) {
            case 200:
                // console.log('rubicGetCompetitionById', response);
                return response;
            default:
                // // console.log('rubicGetCompetitionById', response);
                return response;
        }
    }

    async rubicGetCompetitionByNumber(competitionNumber) {
        const response = await util.fetchApi(`/api/integrations/rubic/competition`, { method: 'GET' }, {
            competitionNumber,
        });
        switch (response.status) {
            case 200:
                // console.log('rubicGetCompetitionByNumber', response);
                return response;
            default:
                // // console.log('rubicGetCompetitionByNumber', response);
                return response;
        }
    }

    async rubicReportResultsToNSF({ competitionId, norgesfeltMax, dryRun = false }) {
        const response = await util.fetchApi(`/api/integrations/rubic/results/report`, { method: 'POST' }, {
            competitionId,
            norgesfeltMax,
            dryRun,
        });
        switch (response.status) {
            case 200:
                console.log('rubicReportResultsToNSF', response);
                return response;
            default:
                // console.log('rubicReportResultsToNSF', response);
                return response;
        }
    }

}

const store = new AppState();

autorun(() => {
    // console.log(store.counter);
})

export default store;
